/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

export enum LemonadeEntrypointSourceEnum {
  PetSelector = 'pet-selector',
  PetProfile = 'pet-profile',
  PostBooking = 'post-booking',
  WhatsNext = 'whats-next',
}

abstract class LemonadePetInsuranceEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.insurance;
}

/**
 * Pet identity and traffic source, carried on every event from the submit CTA onward so
 * that pet segment and acquisition channel can be crossed without joining events back
 * together on person + a time window.
 *
 * `flowSource` here is the `?source=` query param the Pet + User Info page was loaded
 * with, the same value `lemonade-petuserinfo-page-load` reports. It is not restricted to
 * `LemonadeEntrypointSourceEnum`: notification and Iterable campaign links supply their
 * own values.
 */
export type LemonadeFlowContext = {
  /** The selected pet's species, as the plain `dog` / `cat` strings. */
  petType?: PetTypeEnum;
  /** How many pets the owner has on the page. */
  petCount: number;
  /** How many of those pets have a complete enough profile to be quoted. */
  eligiblePetCount: number;
  flowSource?: string | null;
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on the Lemonade Entry point
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 *
 * &nbsp;
 */
export class LemonadeEntryLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-entry-link-click';

  constructor(eventProperties: { flowSource: LemonadeEntrypointSourceEnum }) {
    super(eventProperties, { flowSource: eventProperties.flowSource });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the Lemonade Entry point is viewed/loaded
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 *
 * &nbsp;
 */
export class LemonadeEntryLinkView extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-entry-link-view';

  constructor(eventProperties: { flowSource: LemonadeEntrypointSourceEnum }) {
    super(eventProperties, { flowSource: eventProperties.flowSource });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the Pet + User Info Page is loaded
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoPageLoad extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-page-load';

  constructor(eventProperties: {
    loggedIn: boolean;
    state?: string;
    flowSource?: 'email' | 'entry_point' | string | null;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on add dog or add cat CTA
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoAddPetClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-add-pet-click';

  constructor(eventProperties: { petType: PetTypeEnum }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on pet icon to edit a pet profile
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoEditPetClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-edit-pet-click';

  constructor(eventProperties: { petOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires the first time a user edits their name, email, or birthdate in the owner-info
 * form, distinguishing an engaged-then-abandoned visit from an instant bounce off the
 * page. Deliberately excludes the address fields, which can be set programmatically by
 * the address auto-reconciliation effect in `OwnerInfoForm` without any user action.
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoOwnerFormStart extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-owner-form-start';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on View my price estimate CTA
 *
 * Carries `petOpk` plus the {@link LemonadeFlowContext} properties, so pet segment and
 * traffic source can be read straight off the event.
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-link-click';

  constructor(eventProperties: { petOpk: string } & LemonadeFlowContext) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on Continue to Lemonade CTA
 *
 * This is the last event before the user leaves Rover, so it carries the
 * {@link LemonadeFlowContext} properties: they are what make the hand-off measurable by
 * pet segment and traffic source.
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoRedirectContinueLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-redirect-continue-link-click';

  constructor(eventProperties: LemonadeFlowContext) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on Stay on Rover CTA
 *
 * Carries the same {@link LemonadeFlowContext} properties as the continue event, so the
 * two arms of the redirect decision can be compared on the same cuts.
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoRedirectStayLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-redirect-stay-link-click';

  constructor(eventProperties: LemonadeFlowContext) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on the Sign Up button on the Pet Insurance page
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoSignupClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-signup-click';
}
